import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertOctagon, AlertTriangle, Building2, Check, Clock, Loader2, Megaphone, Plus, Trash2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useJobsite } from "../context/JobsiteContext";
import Sheet from "../components/Sheet";

const PRIORITIES = [
  { value: "normal", label: "Normal", hint: "Regular notice", rank: 2 },
  { value: "important", label: "Important", hint: "Needs attention", rank: 1 },
  { value: "urgent", label: "Urgent", hint: "Read it now", rank: 0 },
];

const PRIORITY_STYLE = {
  normal: {
    bar: "bg-muted-foreground/40",
    card: "border-border",
    pick: "border-primary bg-primary/15 text-primary",
  },
  important: {
    bar: "bg-amber-500",
    card: "border-amber-500/40",
    pick: "border-amber-500 bg-amber-500/15 text-amber-500",
  },
  urgent: {
    bar: "bg-red-500",
    card: "border-red-500/50 bg-red-500/5",
    pick: "border-red-500 bg-red-500/15 text-red-400",
  },
};

function timeAgo(iso) {
  const mins = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

function expiryLabel(iso) {
  if (!iso) return "No expiry";
  const ms = Date.parse(iso) - Date.now();
  if (Number.isNaN(ms)) return "No expiry";
  const days = Math.ceil(ms / 86400000);
  if (days <= 0) return "Expires today";
  if (days === 1) return "Expires tomorrow";
  if (days < 8) return `Expires in ${days} days`;
  return `Expires ${new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" })}`;
}

// Um aviso vale para a obra se nao tiver obras escolhidas (todas) ou se a obra estiver na lista.
function appliesTo(a, siteId) {
  const ids = a.jobsite_ids || [];
  if (ids.length === 0) return true;
  if (!siteId) return true;
  return ids.includes(siteId);
}

export default function Announcements() {
  const { user } = useAuth();
  const { sites, activeSite } = useJobsite();
  const canPost = user?.role === "admin" || user?.role === "supervisor";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const [composeOpen, setComposeOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("normal");
  const [scope, setScope] = useState("all");
  const [picked, setPicked] = useState([]);
  const [expires, setExpires] = useState("");
  const [saving, setSaving] = useState(false);

  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/announcements");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Could not load announcements.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const siteId = activeSite?.id || "";
  const siteNames = useMemo(() => {
    const map = {};
    sites.forEach((s) => {
      map[s.id] = s.name;
    });
    return map;
  }, [sites]);

  const forThisSite = useMemo(() => items.filter((a) => appliesTo(a, siteId)), [items, siteId]);
  const shown = useMemo(() => {
    const list = canPost && filter === "all" ? items : forThisSite;
    const rank = (a) => (PRIORITIES.find((p) => p.value === a.priority) || PRIORITIES[0]).rank;
    return [...list].sort((a, b) => rank(a) - rank(b) || String(b.created_at).localeCompare(String(a.created_at)));
  }, [items, forThisSite, filter, canPost]);

  const openCompose = () => {
    setTitle("");
    setBody("");
    setPriority("normal");
    setScope("all");
    setPicked([]);
    setExpires("");
    setComposeOpen(true);
  };

  const togglePicked = (id) => {
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const post = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error("Add a title and a message.");
      return;
    }
    if (scope === "selected" && picked.length === 0) {
      toast.error("Choose at least one jobsite, or send it to all jobsites.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/announcements", {
        title: title.trim(),
        body: body.trim(),
        priority,
        jobsite_ids: scope === "all" ? [] : picked,
        expires_at: expires ? new Date(`${expires}T23:59:59`).toISOString() : null,
      });
      toast.success("Announcement posted");
      setComposeOpen(false);
      await load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not post the announcement.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/announcements/${deleteId}`);
      setItems((prev) => prev.filter((a) => a.id !== deleteId));
      setDeleteId(null);
      toast.success("Announcement deleted");
    } catch (e) {
      toast.error("Could not delete the announcement.");
    } finally {
      setDeleting(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const fieldClass =
    "h-12 w-full rounded-lg border border-input bg-background px-3 text-base outline-none focus:border-primary";

  const siteChips = (a) => {
    const ids = a.jobsite_ids || [];
    if (ids.length === 0) return "All jobsites";
    const names = ids.map((id) => siteNames[id]).filter(Boolean);
    if (names.length === 0) return "Selected jobsites";
    if (names.length <= 2) return names.join(", ");
    return `${names[0]}, ${names[1]} +${names.length - 2}`;
  };

  return (
    <div className="mx-auto max-w-3xl px-4 pb-32 pt-5 md:px-8 md:pb-10 md:pt-10">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Announcements</h1>
          <p className="mt-1 text-sm text-muted-foreground">Notices for your team, pinned until they expire.</p>
        </div>
        {canPost && (
          <button
            type="button"
            onClick={openCompose}
            className="hidden h-11 shrink-0 items-center gap-2 rounded-lg bg-primary px-4 font-semibold text-black md:inline-flex"
            data-testid="new-announcement-btn"
          >
            <Plus className="h-5 w-5" /> New announcement
          </button>
        )}
      </div>

      {/* Filtro: quem posta ve tudo ou so a obra atual; quem le ve so o que vale para a obra atual */}
      {canPost ? (
        <div className="mb-4 flex gap-2">
          {[
            { id: "all", label: `All (${items.length})` },
            { id: "site", label: `This jobsite (${forThisSite.length})` },
          ].map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={`h-10 rounded-full border px-4 text-sm font-medium ${
                filter === f.id ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      ) : (
        activeSite && (
          <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Building2 className="h-4 w-4" /> Showing notices for {activeSite.name}
          </div>
        )
      )}

      {loading && <div className="py-16 text-center text-sm text-muted-foreground">Loading...</div>}

      {!loading && shown.length === 0 && (
        <div className="grid place-items-center rounded-xl border border-dashed border-border px-6 py-16 text-center">
          <div className="grid h-16 w-16 place-items-center rounded-full bg-primary/10 text-primary">
            <Megaphone className="h-8 w-8" />
          </div>
          <div className="mt-4 text-xl font-semibold">Nothing pinned right now</div>
          <div className="mt-1 max-w-xs text-sm text-muted-foreground">
            {canPost ? "Post a notice and your team will see it here." : "When your team posts a notice, it shows up here."}
          </div>
          {canPost && (
            <button
              type="button"
              onClick={openCompose}
              className="mt-5 inline-flex h-12 items-center gap-2 rounded-lg bg-primary px-5 font-semibold text-black"
            >
              <Plus className="h-5 w-5" /> New announcement
            </button>
          )}
        </div>
      )}

      <div className="space-y-4">
        {shown.map((a) => {
          const style = PRIORITY_STYLE[a.priority] || PRIORITY_STYLE.normal;
          return (
            <article
              key={a.id}
              className={`relative overflow-hidden rounded-xl border bg-card ${style.card}`}
              data-testid={`announcement-${a.id}`}
            >
              <div className={`absolute inset-y-0 left-0 w-1.5 ${style.bar}`} />
              <div className="p-5 pl-6">
                {a.priority === "urgent" && (
                  <div className="mb-2 inline-flex items-center gap-1.5 rounded-md bg-red-500/15 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-red-400">
                    <AlertOctagon className="h-3.5 w-3.5" /> Urgent
                  </div>
                )}
                {a.priority === "important" && (
                  <div className="mb-2 inline-flex items-center gap-1.5 rounded-md bg-amber-500/15 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-amber-500">
                    <AlertTriangle className="h-3.5 w-3.5" /> Important
                  </div>
                )}

                <div className="flex items-start justify-between gap-3">
                  <h2 className="text-lg font-semibold leading-snug">{a.title}</h2>
                  {canPost && (
                    <button
                      type="button"
                      onClick={() => setDeleteId(a.id)}
                      aria-label="Delete announcement"
                      className="-mr-2 -mt-1 grid h-11 w-11 shrink-0 place-items-center rounded-lg text-muted-foreground active:bg-accent"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  )}
                </div>
                <p className="mt-2 whitespace-pre-wrap break-words text-[15px] leading-relaxed text-foreground/90">{a.body}</p>

                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border pt-3 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground/80">{a.author || "Team"}</span>
                  <span>{a.created_at ? timeAgo(a.created_at) : ""}</span>
                  <span className="inline-flex items-center gap-1">
                    <Building2 className="h-3.5 w-3.5" />
                    {siteChips(a)}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {expiryLabel(a.expires_at)}
                  </span>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {/* Botao redondo no celular */}
      {canPost && (
        <button
          type="button"
          onClick={openCompose}
          aria-label="New announcement"
          className="fixed right-4 z-30 grid h-14 w-14 place-items-center rounded-full bg-primary text-black shadow-xl active:scale-95 md:hidden"
          style={{ bottom: "calc(5rem + env(safe-area-inset-bottom))" }}
        >
          <Plus className="h-7 w-7" />
        </button>
      )}

      {composeOpen && (
        <Sheet
          title="New announcement"
          subtitle="Choose who sees it and how important it is."
          onClose={() => !saving && setComposeOpen(false)}
          footer={
            <button
              type="button"
              onClick={post}
              disabled={saving}
              className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-primary text-base font-semibold text-black disabled:opacity-60"
              data-testid="post-announcement"
            >
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Megaphone className="h-5 w-5" />}
              {saving ? "Posting..." : "Post announcement"}
            </button>
          }
        >
          <div className="space-y-5">
            <div>
              <label htmlFor="ann-title" className="mb-1.5 block text-sm font-medium">
                Title
              </label>
              <input
                id="ann-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={fieldClass}
                placeholder="Safety briefing at 7 am"
                maxLength={120}
              />
            </div>

            <div>
              <label htmlFor="ann-body" className="mb-1.5 block text-sm font-medium">
                Message
              </label>
              <textarea
                id="ann-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={4}
                className="w-full resize-none rounded-lg border border-input bg-background px-3 py-3 text-base outline-none focus:border-primary"
                placeholder="Write the details here..."
              />
            </div>

            {/* Importancia */}
            <div>
              <div className="mb-1.5 text-sm font-medium">Importance</div>
              <div className="grid grid-cols-3 gap-2">
                {PRIORITIES.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setPriority(p.value)}
                    className={`rounded-lg border px-2 py-3 text-center ${
                      priority === p.value ? PRIORITY_STYLE[p.value].pick : "border-border text-muted-foreground"
                    }`}
                    data-testid={`priority-${p.value}`}
                  >
                    <div className="text-sm font-semibold">{p.label}</div>
                    <div className="mt-0.5 text-[11px] opacity-80">{p.hint}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Para quais obras */}
            <div>
              <div className="mb-1.5 text-sm font-medium">Send to</div>
              <div className="space-y-2">
                {[
                  { id: "all", title: "All jobsites", hint: "Everyone in the company sees it" },
                  { id: "selected", title: "Choose jobsites", hint: "Only the jobsites you pick" },
                ].map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setScope(o.id)}
                    className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left ${
                      scope === o.id ? "border-primary bg-primary/10" : "border-border"
                    }`}
                  >
                    <span
                      className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border ${
                        scope === o.id ? "border-primary" : "border-muted-foreground/50"
                      }`}
                    >
                      {scope === o.id && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold">{o.title}</span>
                      <span className="block text-xs text-muted-foreground">{o.hint}</span>
                    </span>
                  </button>
                ))}
              </div>

              {scope === "selected" && (
                <div className="mt-3">
                  {sites.length === 0 ? (
                    <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">No jobsites yet.</div>
                  ) : (
                    <>
                      <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
                        <span>{picked.length} selected</span>
                        <button
                          type="button"
                          onClick={() => setPicked(picked.length === sites.length ? [] : sites.map((s) => s.id))}
                          className="py-1 font-medium text-primary"
                        >
                          {picked.length === sites.length ? "Clear all" : "Select all"}
                        </button>
                      </div>
                      <div className="space-y-2">
                        {sites.map((s) => {
                          const on = picked.includes(s.id);
                          return (
                            <button
                              key={s.id}
                              type="button"
                              role="checkbox"
                              aria-checked={on}
                              onClick={() => togglePicked(s.id)}
                              className={`flex min-h-[48px] w-full items-center gap-3 rounded-lg border px-4 py-2 text-left ${
                                on ? "border-primary bg-primary/10" : "border-border"
                              }`}
                            >
                              <span
                                className={`grid h-6 w-6 shrink-0 place-items-center rounded-md border ${
                                  on ? "border-primary bg-primary text-black" : "border-muted-foreground/50"
                                }`}
                              >
                                {on && <Check className="h-4 w-4" strokeWidth={3} />}
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-semibold">{s.name}</span>
                                {s.address && <span className="block truncate text-xs text-muted-foreground">{s.address}</span>}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div>
              <label htmlFor="ann-expires" className="mb-1.5 block text-sm font-medium">
                Expires <span className="font-normal text-muted-foreground">(optional)</span>
              </label>
              <input
                id="ann-expires"
                type="date"
                min={today}
                value={expires}
                onChange={(e) => setExpires(e.target.value)}
                className={fieldClass}
              />
            </div>
          </div>
        </Sheet>
      )}

      {deleteId && (
        <Sheet
          title="Delete this announcement?"
          subtitle="It disappears for everyone on the team."
          onClose={() => !deleting && setDeleteId(null)}
          footer={
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setDeleteId(null)}
                disabled={deleting}
                className="h-12 flex-1 rounded-xl border border-border text-base font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={remove}
                disabled={deleting}
                className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-destructive text-base font-semibold text-destructive-foreground disabled:opacity-60"
              >
                {deleting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Trash2 className="h-5 w-5" />}
                Delete
              </button>
            </div>
          }
        >
          <p className="text-sm text-muted-foreground">This cannot be undone.</p>
        </Sheet>
      )}
    </div>
  );
}
